import {Trash2} from 'lucide';
import React, {useCallback, useMemo} from 'react';
import {NavLink, useLocation, useNavigate} from 'react-router-dom';
import {Breadcrumbs} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {Input} from '~/components/Controls/Input';
import {ToggleGroup} from '~/components/Controls/ToggleGroup';
import {useConfirm, useMessage} from '~/components/Dialogs';
import {onSubmitError} from '~/components/Form';
import {LucideIcon} from '~/components/LucideIcon';
import {useModals} from '~/components/Modal';
import {Progress} from '~/components/Progress';
import {TableRowActions, TableSort, TableSortDirection} from '~/components/Table';
import {Title} from '~/components/Title';
import {useAbility} from '~/lib/ability';
import {CSV} from '~/lib/csv';
import {getApiErrorMessage} from '~/lib/errors/apiError';
import {usePersistedSearch} from '~/lib/usePersistedSearch';
import {useDataSource} from '~/lib/dataSource';
import {renderBoolean, renderToggle} from '~/lib/helpers';
import {Intent} from '~/lib/intent';
import {useQueryResource, useResource} from '~/lib/loader';
import {sortByBoolean, sortByNumber, sortByString} from '~/lib/sort';
import {clientsLinks} from '~/modules/Clients';
import {ClientsAbility} from '~/modules/Clients/ability';
import {agenciesAdapter} from '~/modules/Clients/adapters';
import {
    activateAllAgencies,
    bulkAssignAgencyPreset,
    createGlobalTagIdConfig,
    deactivateAllAgencies,
    fetchAgency,
    fetchAgencies,
    getGlobalTagIdConfig,
    IAgenciesFilter,
    toggleAgency,
} from '~/modules/Clients/api';
import {CopyAgencyForm} from '~/modules/Clients/forms/CopyAgencyForm';
import {copyAgency} from '~/modules/Bidders/api';
import {ClientsTypeNavigation} from '~/modules/Clients/components/ClientsTypeNavigation';
import {MultiSelectDspConnectionType} from '~/modules/Clients/components/SelectDspConnectionType';
import {SelectAccountManagerAgency} from '~/modules/Clients/components/SelectAccountManagerAgency';
import {AgencyForm} from '~/modules/Clients/forms/AgencyForm';
import {GlobalAgencyConfigForm} from '~/modules/Clients/forms/GlobalAgencyConfigForm';
import {PresetBulkAssignForm} from '~/modules/Clients/forms/PresetBulkAssignForm';
import {defaultAgency} from '~/modules/Clients/models/Agency';
import {useDspConnectionTypesQuery, useAgencyAccountManagersQuery} from '~/modules/Dictionary/queries';
import style from './index.module.scss';

const agencyStatusItems = [
    {label: 'All', value: 'all'},
    {label: 'Active', value: 'active'},
    {label: 'Inactive', value: 'inactive'},
] as const;

type AgencyStatusFilter = (typeof agencyStatusItems)[number]['value'];

const dspModeItems = [
    {label: 'Include', value: 'include'},
    {label: 'Exclude', value: 'exclude'},
] as const;

type DspMode = (typeof dspModeItems)[number]['value'];

export const AgenciesView = () => {
    const {createRecord, updateRecord, deleteRecord} = useDataSource(agenciesAdapter);
    const globalConfigResult = useResource(() => getGlobalTagIdConfig());
    const {data: dspConnectionTypes = []} = useDspConnectionTypesQuery();
    const {data: accountManagers = [], isPending: isAccountManagersPending} = useAgencyAccountManagersQuery();
    const navigate = useNavigate();
    const location = useLocation();
    const modal = useModals();
    const confirm = useConfirm();
    const message = useMessage();
    const ability = useAbility(ClientsAbility);

    const effectiveSearch = usePersistedSearch('agencies.filter');

    const queryParams = useMemo(() => new URLSearchParams(effectiveSearch), [effectiveSearch]);

    const setQueryParams = useCallback((params: Record<string, string | null>) => {
        const next = new URLSearchParams(location.search);
        for (const [key, value] of Object.entries(params)) {
            if (value === null || value === '') {
                next.delete(key);
            } else {
                next.set(key, value);
            }
        }
        navigate({search: next.toString()}, {replace: true});
    }, [location.search, navigate]);

    const filter = useMemo<IAgenciesFilter>(() => {
        const statusParam = queryParams.get('status');
        const dspParam = queryParams.get('dsp');
        const dspConnectionType = dspParam ? dspParam.split(',').filter(Boolean) : undefined;
        const amParam = queryParams.get('am');
        return {
            id: queryParams.get('id') || undefined,
            title: queryParams.get('title') || '',
            active: statusParam === 'active' ? true : statusParam === 'inactive' ? false : undefined,
            dspConnectionType: dspConnectionType ?? [],
            dspConnectionTypeExclude: queryParams.get('dspExclude') === 'true',
            accountManagerId: amParam ? Number(amParam) : undefined,
        };
    }, [queryParams]);

    const accountManagerById = useMemo(() => {
        const map = new Map<number, string>();
        accountManagers.forEach((m) => {
            if (m?.id != null) map.set(m.id, m.name);
        });
        return map;
    }, [accountManagers]);

    const dspLabelByName = useMemo(() => {
        const map = new Map<string, string>();
        dspConnectionTypes.forEach((t) => {
            if (t?.name) {
                map.set(t.name, t.label || t.name);
            }
        });
        return map;
    }, [dspConnectionTypes]);

    const result = useQueryResource(
        () => fetchAgencies(filter),
        [filter.id, filter.title, filter.active, JSON.stringify(filter.dspConnectionType), filter.dspConnectionTypeExclude, filter.accountManagerId],
        [] as any
    );

    const createAgency = modal.create(AgencyForm, {
        onSubmit: (agency, formikActions, close) => {
            createRecord(agency).then(createdAgency => {
                close();
                result.fetch();
                navigate(clientsLinks.agency(createdAgency.id));
            }).catch(onSubmitError(formikActions));
        }
    });

    const editAgency = modal.create(AgencyForm, {
        onSubmit: (agency, formikActions, close) => {
            updateRecord(agency).then(() => {
                close();
                result.fetch();
            }).catch(onSubmitError(formikActions));
        }
    });

    const copyAgencyForm = modal.create(CopyAgencyForm, {});

    const presetBulkAssignForm = modal.create(PresetBulkAssignForm, {
        side: 'demand',
        onSubmit: ({resourceIds, presetId}, formActions, close) => {
            if (presetId == null) return;
            bulkAssignAgencyPreset(resourceIds, presetId)
                .then(() => {
                    close();
                    result.fetch();
                })
                .catch(error => formActions.setStatus(getApiErrorMessage(error, 'Failed to assign preset')));
        }
    });

    const globalConfigForm = modal.create(GlobalAgencyConfigForm, {
        onSubmit: (configuration, formikActions, close) => {
            const configObject = typeof configuration.config === 'string'
                ? JSON.parse(configuration.config)
                : configuration.config;

            createGlobalTagIdConfig(configObject)
                .then(globalConfigResult.fetch)
                .then(close)
                .catch(onSubmitError(formikActions));
        }
    });

    const handleIdChange = (value) => {
        setQueryParams({id: value || null});
    };

    const handleNameChange = (value) => {
        setQueryParams({title: value || null});
    };

    const statusValue: AgencyStatusFilter = filter.active === true ? 'active' : filter.active === false ? 'inactive' : 'all';
    const handleStatusChange = (value: AgencyStatusFilter) => {
        setQueryParams({status: value === 'all' ? null : value});
    };

    const dspModeValue: DspMode = filter.dspConnectionTypeExclude ? 'exclude' : 'include';
    const handleDspModeChange = (value: DspMode) => {
        setQueryParams({dspExclude: value === 'exclude' ? 'true' : null});
    };

    return <div>
        <div className="page-header-sticky">
            <div className="page-header-breadcrumbs">
                <Breadcrumbs/>
            </div>
            <Title className="page-header-title" toolbar={<>
                {ability.canEditGlobalAgencyConfig() &&
                    <Button key='global-config' responsive disabled={result.isPending || globalConfigResult.isPending} intent={Intent.SUCCESS}
                        leftIcon={<i className='fa fa-globe'/>}
                        onClick={() => globalConfigForm.show({configuration: globalConfigResult.resource})}>TagId config</Button>}
                {ability.canBulkAssignAgencyPreset() &&
                    <Button key='bulk-preset' responsive disabled={result.isPending} intent={Intent.SUCCESS}
                        leftIcon={<i className='fa fa-table'/>}
                        onClick={() => presetBulkAssignForm.show()}>Preset</Button>}
                {ability.canCreateAgency() &&
                    <Button key='create' disabled={result.isPending} responsive intent={Intent.PRIMARY}
                        leftIcon={<i className='fas fa-plus'/>}
                        onClick={() => createAgency.show({agency: defaultAgency})}>Create</Button>}
                <Button key='reload' disabled={result.isPending} responsive intent={Intent.SUCCESS}
                    leftIcon={<i className='fas fa-sync-alt'/>} onClick={() => result.fetch()}>Reload</Button>
                <Button responsive disabled={result.isPending}
                    onClick={() => CSV.fromDataSource(result.resource, ['id', 'title', 'externalId', 'isActive']).download('agencies.csv')}
                    leftIcon={<i className='far fa-file-excel'/>}
                >Export</Button>
            </>}>Agencies</Title>
        </div>
        <ClientsTypeNavigation/>
        <div className="layout-content-box">
            <form style={{display: 'flex', gap: '10px', marginBottom: '10px'}} onSubmit={(e) => {
                e.preventDefault();
            }}>
                <Input
                    value={filter.id || ''}
                    onChange={handleIdChange}
                    placeholder='Search by ID'
                    rightIcon={<i className='fa fa-search input-search-icon' />}
                />
                <Input
                    value={filter.title}
                    onChange={handleNameChange}
                    placeholder='Search by title'
                    rightIcon={<i className='fa fa-search input-search-icon' />}
                />
               <div className={style.dspFilterGroup}>
                    <ToggleGroup
                        className={style.dspToggleJoined}
                        ariaLabel="DSP filter mode"
                        items={dspModeItems}
                        value={dspModeValue}
                        onChange={handleDspModeChange}
                    />
                    <MultiSelectDspConnectionType
                        className={style.dspSelectJoined}
                        value={filter.dspConnectionType}
                        showRawName={false}
                        collapsible
                        onChange={(vals) => {
                            setQueryParams({dsp: vals.length ? vals.join(',') : null});
                        }}
                    />
                </div>
                <div style={{minWidth: 120}}>
                    <SelectAccountManagerAgency
                        value={filter.accountManagerId ?? null}
                        placeholder="Account Manager"
                        onChange={(val) => setQueryParams({am: val != null ? String(val) : null})}
                    />
                </div>
                <ToggleGroup
                    ariaLabel="Status"
                    items={agencyStatusItems}
                    value={statusValue}
                    onChange={handleStatusChange}
                />
                <Button
                    onClick={() => setQueryParams({id: null, title: null, status: null, dsp: null, dspExclude: null, am: null})}
                >
                    Reset
                </Button>
            </form>
            <TableSort records={result.resource}
            header
            defaultSort={{
                sort: sortByNumber,
                field: 'id',
                direction: TableSortDirection.DESC
            }}
            columns={[{
                header: 'ID',
                small: true,
                field: 'id',
                sort: sortByNumber
            }, {
                header: 'Title',
                field: 'title',
                sort: sortByString,
                cell: agency => <NavLink to={clientsLinks.agency(agency.id)}>{agency.title}</NavLink>
            }, {
                header: 'DSP Connection Type',
                field: 'dspConnectionType',
                sort: sortByString,
                cell: (agency) => dspLabelByName.get(agency.dspConnectionType) ?? agency.dspConnectionType,
            }, ...(filter.dspConnectionType.length ? [{
                header: 'P2P Coefficient',
                field: 'p2pCoefficient',
                sort: sortByNumber,
                cell: (agency) => agency.p2pCoefficient ?? '—',
            }] : []), {
                header: 'Account Manager',
                field: 'accountManagerId',
                cell: (agency) => agency.accountManagerId != null ? (isAccountManagersPending ? null : accountManagerById.get(agency.accountManagerId) ?? agency.accountManagerId) : null,
            }, {
                header: STAGING ? result.resource.find(a => a.isActive) ?
                    <Button small key='deactivateAll' disabled={result.isPending} responsive intent={Intent.DANGER}
                        onClick={(e) => {
                            e.stopPropagation();
                            deactivateAllAgencies().then(() => result.fetch());
                        }}>Deactivate all</Button> :
                    <Button small key='activateAll' disabled={result.isPending} responsive
                        intent={Intent.WARNING}
                        onClick={(e) => {
                            e.stopPropagation();
                            activateAllAgencies().then(() => result.fetch());
                        }}>Activate all</Button> : 'Active',
                field: 'isActive',
                headerAttrs: {style: {minWidth: 160}},
                cellAttrs: {style: {minWidth: 160}},
                cell: agency => ability.canEditAgency(agency) ? renderToggle(agency.isActive,
                    () => confirm(agency.isActive ? 'Deactivate agency?' : 'Activate agency?', {
                        onOk: () => toggleAgency(agency, !agency.isActive)
                            .then(() => result.fetch()).catch(() => message('Can\'t activate agency', {
                                intent: Intent.DANGER,
                                icon: <i className='fa fa-times'/>
                            }
                            )),
                        icon: <i className='fa fa-question'/>,
                        intent: Intent.WARNING
                    })) : renderBoolean(agency.isActive),
                sort: sortByBoolean
            }, {
                small: true,
                cell: (agency) => <TableRowActions>
                    {ability.canCopyAgency(agency) && (
                        <Button
                            small
                            variant="menu"
                            onClick={() => copyAgencyForm.show({
                                agencyId: agency.id,
                                onSubmit: (body, formikActions, close) => {
                                    copyAgency(agency.id, body)
                                        .then(() => { result.fetch(); close(); })
                                        .catch(onSubmitError(formikActions));
                                },
                            }, {size: 'wide'})}
                            leftIcon={<i className='far fa-copy'/>}
                        >
                            Clone
                        </Button>
                    )}
                    {ability.canEditAgency(agency) && (
                        <Button
                            small
                            variant="menu"
                            onClick={() => fetchAgency(agency.id).then(fullAgency => editAgency.show({agency: fullAgency}))}
                            leftIcon={<i className='fa fa-edit'/>}
                        >
                            Edit
                        </Button>
                    )}
                    {ability.canDeleteAgency(agency) && (
                        <Button
                            small
                            variant="menu"
                            intent={Intent.DANGER}
                            onClick={() => confirm('Delete agency?', {
                                onOk: () => deleteRecord(agency).then(() => result.fetch()),
                                icon: <LucideIcon icon={Trash2} size={20}/>,
                                intent: Intent.DANGER
                            })}
                            leftIcon={<i className='fa fa-trash'/>}
                        >
                            Delete
                        </Button>
                    )}
                </TableRowActions>
            }]}
            placeholder={result.isPending && (!result.resource || !result.resource.length) && <Progress/>}
            />
        </div>
    </div>;
};
